import { chmod, open, readFile, rename, rm } from "node:fs/promises";
import { randomUUID } from "node:crypto";
import { join } from "node:path";
import {
  encryptBackup,
  hashBackupCode,
  bytesToBase64,
  bytesToHex,
  type BackupBundle,
} from "@agentvault/crypto";
import type { PersistedState } from "./types.js";
import { ensureSecureDir, FILE_MODE, writeSecureFile } from "./secure-file.js";

const STATE_FILE = "agentvault.json";
const LEGACY_STATE_FILE = "secure-channel.json";

// State files contain deviceJwt and ratchet keys — restrict to owner only.
// Modes live in secure-file.ts, which enforces them on files that ALREADY
// exist too (#622 — a `mode:` option alone only applies at creation).

export async function saveState(
  dataDir: string,
  state: PersistedState,
  options: {
    signal?: AbortSignal;
    /** Last lifecycle check before the atomic rename publishes new credentials. */
    commitGuard?: () => boolean | Promise<boolean>;
  } = {},
): Promise<void> {
  await ensureSecureDir(dataDir);
  const filePath = join(dataDir, STATE_FILE);
  const tempPath = `${filePath}.tmp-${process.pid}-${randomUUID()}`;
  let handle: Awaited<ReturnType<typeof open>> | null = null;
  try {
    options.signal?.throwIfAborted();
    handle = await open(tempPath, "wx", FILE_MODE);
    await handle.writeFile(JSON.stringify(state, null, 2), "utf8");
    await handle.sync();
    await handle.close();
    handle = null;
    // Creation mode is umask-sensitive and an existing temp path must never be
    // trusted. Enforce owner-only access before it can become the primary file.
    await chmod(tempPath, FILE_MODE);

    const mayCommit = await options.commitGuard?.() ?? true;
    options.signal?.throwIfAborted();
    if (!mayCommit) {
      throw new DOMException("State publication cancelled", "AbortError");
    }
    // Publication is one filesystem operation: the previous credential file
    // remains complete until this rename succeeds.
    await rename(tempPath, filePath);
    await chmod(filePath, FILE_MODE);

    // Best-effort directory sync makes the rename durable across a host crash.
    try {
      const dir = await open(dataDir, "r");
      try {
        await dir.sync();
      } finally {
        await dir.close();
      }
    } catch {
      // Some platforms do not permit opening directories. Atomicity still
      // comes from rename; durability falls back to the filesystem defaults.
    }
  } finally {
    await handle?.close().catch(() => {});
    await rm(tempPath, { force: true }).catch(() => {});
  }

  // Clean up legacy file after migration
  try {
    await rm(join(dataDir, LEGACY_STATE_FILE));
  } catch {
    // Already removed or never existed
  }
}

/**
 * Load raw persisted state from disk.
 * Returns the raw parsed JSON — caller is responsible for migration
 * from legacy format to current PersistedState.
 *
 * Checks agentvault.json first, falls back to secure-channel.json
 * for backward compatibility. Auto-migrates on first legacy load.
 */
export async function loadState(
  dataDir: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any | null> {
  // Try new filename first
  const filePath = join(dataDir, STATE_FILE);
  try {
    const raw = await readFile(filePath, "utf-8");
    return JSON.parse(raw);
  } catch {
    // Fall through to legacy
  }

  // Fall back to legacy filename
  const legacyPath = join(dataDir, LEGACY_STATE_FILE);
  try {
    const raw = await readFile(legacyPath, "utf-8");
    const parsed = JSON.parse(raw);
    // Auto-migrate: write to new filename, remove legacy
    await ensureSecureDir(dataDir);
    await writeSecureFile(filePath, JSON.stringify(parsed, null, 2));
    await rm(legacyPath);
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Check whether persisted state is valid and usable (has deviceId, deviceJwt, and at least one session).
 */
export async function isStateValid(dataDir: string): Promise<boolean> {
  try {
    const filePath = join(dataDir, STATE_FILE);
    const raw = await readFile(filePath, "utf-8");
    const parsed = JSON.parse(raw);
    return !!(
      parsed &&
      parsed.deviceId &&
      parsed.deviceJwt &&
      parsed.sessions &&
      Object.keys(parsed.sessions).length > 0
    );
  } catch {
    return false;
  }
}

/**
 * Create a backup of the state file (atomic write via tmp + rename).
 * Silent no-op if no state file exists.
 */
export async function backupState(dataDir: string): Promise<void> {
  const src = join(dataDir, STATE_FILE);
  const bak = join(dataDir, `${STATE_FILE}.bak`);
  const tmp = join(dataDir, `${STATE_FILE}.bak.tmp`);
  try {
    const data = await readFile(src, "utf-8");
    await writeSecureFile(tmp, data);
    await rename(tmp, bak);
  } catch {
    // No state file or write failed — silent no-op
  }
}

/**
 * Restore state from backup if primary state is missing/empty/invalid but .bak exists and is valid.
 * Returns true if state was successfully restored.
 */
export async function restoreState(dataDir: string): Promise<boolean> {
  // Only restore if primary is missing or invalid
  const primaryValid = await isStateValid(dataDir);
  if (primaryValid) return false;

  const bak = join(dataDir, `${STATE_FILE}.bak`);
  try {
    const data = await readFile(bak, "utf-8");
    const parsed = JSON.parse(data);
    if (
      !parsed ||
      !parsed.deviceId ||
      !parsed.deviceJwt ||
      !parsed.sessions ||
      Object.keys(parsed.sessions).length === 0
    ) {
      return false;
    }
    await ensureSecureDir(dataDir);
    await writeSecureFile(join(dataDir, STATE_FILE), data);
    return true;
  } catch {
    return false;
  }
}

/**
 * Upload encrypted backup to server. Converts PersistedState → BackupBundle,
 * encrypts with backup code, and PUTs to the backend.
 */
export async function uploadBackupToServer(
  state: PersistedState,
  backupCode: string,
  apiUrl: string,
  deviceJwt: string,
): Promise<void> {
  // Build BackupBundle from PersistedState
  const ratchets: Record<string, string> = {};
  for (const [convId, session] of Object.entries(state.sessions)) {
    if (session.ratchetState) {
      ratchets[convId] = session.ratchetState;
    }
  }

  const bundle: BackupBundle = {
    version: 1,
    createdAt: new Date().toISOString(),
    deviceId: state.deviceId,
    identityKeypair: state.identityKeypair,
    ephemeralKeypair: state.ephemeralKeypair,
    fingerprint: state.fingerprint,
    ratchets,
    sessions: Object.fromEntries(
      Object.entries(state.sessions).map(([convId, s]) => [
        convId,
        {
          ownerDeviceId: s.ownerDeviceId,
          ratchetState: s.ratchetState,
          activated: s.activated,
        },
      ]),
    ),
    topics: state.topics,
    defaultTopicId: state.defaultTopicId,
    groupId: state.groupId,
    hubAddress: state.hubAddress,
  };

  const encrypted = await encryptBackup(bundle, backupCode);
  const codeHash = await hashBackupCode(backupCode);

  const resp = await fetch(`${apiUrl}/api/v1/backup`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${deviceJwt}`,
    },
    body: JSON.stringify({
      backup_blob: bytesToBase64(encrypted),
      backup_code_hash: bytesToHex(codeHash),
    }),
  });

  if (!resp.ok) {
    throw new Error(`Backup upload failed: ${resp.status}`);
  }
}

export async function clearState(dataDir: string): Promise<void> {
  // Remove both files to cover migration edge cases
  for (const filename of [STATE_FILE, LEGACY_STATE_FILE]) {
    try {
      await rm(join(dataDir, filename));
    } catch {
      // File doesn't exist — nothing to clear
    }
  }
}
